// nono-stories.js: player v2 for member films (snippets/c-story-player, owner-approved 19.10.2026, Figma 1669:8023).
// <nono-stories> wraps a row of c-video-tile (story: true) tiles and one c-story-player dialog. Every film in the row
// plays in that one player, in order. Two <video> elements take turns: the next film buffers while this one plays.
// Sources: Shopify-hosted films (data-ladder) play the progressive MP4 first (measured 19.10: 2-3x sooner first frame
// than HLS, sharp from frame one), HLS as fallback; own encodes (AV1 → HEVC → H.264) the first type that plays.
// No dependencies.

const HLS = /mpegurl/i;
const MUTE_KEY = 'nono_story_muted';
const parse = (s) => { try { return JSON.parse(s); } catch { return null; } };
const store = (k, v) => { try { if (v === undefined) return sessionStorage.getItem(k); sessionStorage.setItem(k, v); } catch { /* blocked */ } return null; };

/** The source to play for one tile: crop by window shape, MP4 rendition by size, else the first playable type. */
function sourceFor(el, video) {
  let list = parse(el.dataset.sources);
  const landscape = innerWidth > innerHeight;
  if (list && !Array.isArray(list)) list = list[landscape ? 'pc' : 'mobile'] || list.pc || list.mobile;
  list = (list || []).filter((s) => s && s.src && video.canPlayType(s.type || 'video/mp4'));
  let pick = null;
  if (el.hasAttribute('data-ladder')) {
    const mp4 = list.filter((s) => !HLS.test(s.type || '')).sort((a, b) => (a.w || 0) - (b.w || 0));
    const want = Math.min(innerWidth, 460) * Math.min(devicePixelRatio || 1, 2);
    pick = mp4.find((s) => (s.w || 0) >= want) || mp4[mp4.length - 1] || list[0];
  } else pick = list[0];
  if (!pick) return '';
  return /^https?:|^\/\//.test(pick.src) ? pick.src : (el.dataset.base || '') + pick.src;
}

const captionsFor = (el) =>
  (innerWidth > innerHeight ? el.dataset.captionsPc : el.dataset.captionsMobile) || el.dataset.captions || '';

class NonoStories extends HTMLElement {
  connectedCallback() {
    if (this.dialog) return;
    this.dialog = this.querySelector('[data-story-dialog]');
    this.films = [...this.querySelectorAll('nono-player[data-story]')];
    if (!this.dialog || !this.films.length) return;
    const css = this.dialog.dataset.css;
    if (css && !document.querySelector(`link[href="${css}"]`)) {
      const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: css });
      document.head.append(link);
    }
    const $ = (s) => this.dialog.querySelector(s);
    this.videos = [...this.dialog.querySelectorAll('[data-story-video]')];
    this.active = 0;
    this.index = 0;
    this.muted = store(MUTE_KEY) === '1';
    this.cc = false;
    this.ui = { progress: $('[data-story-progress]'), context: $('[data-story-context]'), count: $('[data-story-count]'),
      quote: $('[data-story-quote]'), pause: $('[data-story-pause]'), mute: $('[data-story-mute]'), ccBtn: $('[data-story-cc]'),
      prev: this.dialog.querySelector('.c-story__nav--prev'), next: this.dialog.querySelector('.c-story__nav--next') };
    this.ui.progress.replaceChildren(...this.films.map(() => {
      const seg = document.createElement('span');
      seg.className = 'c-story__seg';
      seg.append(document.createElement('span'));
      return seg;
    }));

    this.films.forEach((film, i) => {
      const btn = film.querySelector('[data-story-open]');
      if (!btn) return;
      btn.addEventListener('click', () => this.open(i, btn));
      let hover = 0;
      btn.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hover = setTimeout(() => this.warm(i), 120); });
      btn.addEventListener('pointerleave', () => clearTimeout(hover));
      btn.addEventListener('pointerdown', () => this.warm(i));
    });

    this.dialog.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      if (t === this.dialog) return this.dialog.close();
      if (t.closest('[data-story-close]')) return this.dialog.close();
      if (t.closest('[data-story-prev]')) return this.go(this.index - 1);
      if (t.closest('[data-story-next]')) return this.go(this.index + 1);
      if (t.closest('[data-story-toggle]') || t.closest('[data-story-pause]')) return this.toggle();
      if (t.closest('[data-story-mute]')) return this.setMuted(!this.muted);
      if (t.closest('[data-story-cc]')) return this.setCaptions(!this.cc);
      // The booking CTA: close the player first, then the delegated booking listener opens the booking window.
      if (t.closest('[data-story-cta]')) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      this.videos.forEach((v) => v.pause());
      cancelAnimationFrame(this.raf);
      if (this.opener?.isConnected) this.opener.focus();
    });
    this.dialog.addEventListener('keydown', (e) => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      else if (e.key === 'm' || e.key === 'M') this.setMuted(!this.muted);
      else if (e.key === ' ' && !(e.target instanceof HTMLButtonElement || e.target instanceof HTMLAnchorElement)) { e.preventDefault(); this.toggle(); }
    });
    let x0 = null;
    let y0 = 0;
    const stage = this.dialog.querySelector('[data-story-stage]');
    stage.addEventListener('touchstart', (e) => { if (e.touches.length === 1) { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; } }, { passive: true });
    stage.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) { e.preventDefault(); this.go(this.index + (dx < 0 ? 1 : -1)); }
    });

    this.videos.forEach((v) => {
      v.addEventListener('playing', () => { if (v === this.video) { this.dialog.removeAttribute('data-loading'); this.sync(); this.buffer(); } });
      v.addEventListener('waiting', () => { if (v === this.video && this.dialog.open) this.dialog.setAttribute('data-loading', ''); });
      v.addEventListener('pause', () => { if (v === this.video) this.sync(); });
      v.addEventListener('ended', () => { if (v === this.video) this.ended(); });
      v.addEventListener('error', () => { if (v === this.video && this.index < this.films.length - 1) this.go(this.index + 1); });
    });
    this.setMuted(this.muted, true);
  }

  get video() { return this.videos[this.active]; }
  get spare() { return this.videos[1 - this.active]; }

  /** Puts film i's source on a video element (once) and lets it buffer. */
  load(video, i) {
    const film = this.films[i];
    const src = film && sourceFor(film, video);
    if (!src) return false;
    if (video.dataset.film !== String(i)) {
      video.dataset.film = String(i);
      video.dataset.name = film.dataset.quote || film.dataset.context || `Film ${i + 1}`; // GA4 video_name
      video.querySelectorAll('track').forEach((t) => t.remove());
      const cap = captionsFor(film);
      if (cap) {
        const t = document.createElement('track');
        Object.assign(t, { kind: 'captions', srclang: 'no', label: 'Norsk', src: cap });
        video.append(t);
      }
      if (film.dataset.poster) video.poster = film.dataset.poster;
      video.preload = 'auto';
      video.src = src;
    }
    return true;
  }

  warm(i) { if (!this.dialog.open) this.load(this.video, i); }

  /** Buffers the next film on the spare element while this one plays. */
  buffer() {
    const n = this.index + 1;
    if (n < this.films.length) { this.spare.muted = true; this.load(this.spare, n); }
  }

  open(i, opener) {
    document.querySelectorAll('nono-loop').forEach((l) => l.pause?.(false));
    this.opener = opener;
    this.dialog.showModal();
    this.show(i, true);
  }

  go(i) {
    if (i < 0 || i >= this.films.length || i === this.index) return;
    this.show(i, false);
  }

  show(i, first) {
    this.video.pause();
    if (!first && this.spare.dataset.film === String(i)) this.active = 1 - this.active;
    this.videos.forEach((v, k) => v.classList.toggle('is-active', k === this.active));
    this.index = i;
    this.dialog.setAttribute('data-loading', '');
    this.dialog.removeAttribute('data-ended');
    const v = this.video;
    if (!this.load(v, i)) return;
    if (v.currentTime > 0 && (v.ended || first)) v.currentTime = 0;
    v.muted = this.muted;
    this.applyCaptions();
    this.render();
    v.play().catch(() => {
      // Sound before a fresh gesture can be refused (iOS): play muted and show the sound button as off.
      this.setMuted(true);
      v.play().catch(() => this.dialog.removeAttribute('data-loading'));
    });
    this.tick();
  }

  ended() {
    if (this.index < this.films.length - 1) this.go(this.index + 1);
    else { this.dialog.setAttribute('data-ended', ''); this.sync(); }
  }

  toggle() {
    const v = this.video;
    if (v.paused) { if (v.ended) v.currentTime = 0; v.play().catch(() => {}); } else v.pause();
  }

  setMuted(m, quiet) {
    this.muted = m;
    if (!quiet) store(MUTE_KEY, m ? '1' : '0');
    if (this.videos) this.video.muted = m;
    this.dialog.toggleAttribute('data-muted', m);
    const d = this.dialog.dataset;
    this.ui.mute.setAttribute('aria-label', m ? d.labelUnmute : d.labelMute);
  }

  setCaptions(on) { this.cc = on; this.applyCaptions(); }

  applyCaptions() {
    const tracks = this.video.textTracks;
    for (const t of tracks) t.mode = this.cc ? 'showing' : 'hidden';
    const has = !!captionsFor(this.films[this.index]);
    this.ui.ccBtn.hidden = !has;
    this.ui.ccBtn.setAttribute('aria-pressed', String(this.cc));
  }

  render() {
    const film = this.films[this.index];
    const n = this.films.length;
    this.ui.context.textContent = film.dataset.context || '';
    const count = (this.dialog.dataset.counter || '').replace('[n]', this.index + 1).replace('[total]', n);
    this.ui.count.textContent = film.dataset.duration ? `${count} · ${film.dataset.duration}` : count;
    this.ui.quote.textContent = film.dataset.quote || '';
    this.ui.prev.disabled = this.index === 0;
    this.ui.next.disabled = this.index === n - 1;
    [...this.ui.progress.children].forEach((seg, k) => seg.firstChild.style.setProperty('--p', k < this.index ? 1 : 0));
  }

  sync() {
    const paused = this.video.paused;
    this.dialog.toggleAttribute('data-paused', paused);
    const d = this.dialog.dataset;
    this.ui.pause.setAttribute('aria-label', paused ? d.labelPlay : d.labelPause);
  }

  /** Progress of the playing film, once per frame while the dialog is open. */
  tick() {
    cancelAnimationFrame(this.raf);
    const step = () => {
      const v = this.video;
      const seg = this.ui.progress.children[this.index];
      if (seg && v.duration) seg.firstChild.style.setProperty('--p', Math.min(1, v.currentTime / v.duration).toFixed(4));
      if (this.dialog.open) this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }
}

customElements.get('nono-stories') || customElements.define('nono-stories', NonoStories);
